/**
 * ENG-5727 — why this viewer may not open an engagement, and what to show.
 *
 * The rule itself is NOT here. It lives in the api
 * (`engagement/service/create_authority.go`) and arrives as
 * `Project.viewer_engagement_create_denial`, because the webapp was previously
 * the only place any of it was enforced: `engagement:create` is granted by the
 * global staff wildcard regardless of resource, and `Create` never checked
 * entitlement at all. A predicate here that re-derived the answer from `viewer`
 * and `group` would recreate exactly that split.
 *
 * So this module only decides PRESENTATION, and the one thing it adds is the
 * distinction a boolean cannot carry: whether a refusal should be hidden or
 * explained.
 */
import { EngagementCreateDenialReason } from '@/__generated__/graphql';
// The codes module directly, not the `@/core/api` barrel, which would drag the
// Apollo client into a module that is otherwise pure and is imported by the
// consultation header.
import ERROR_CODES from '@/core/apiErrorCodes';

/**
 * Names for the refusal copy. The account is the GROUP's name, never the
 * billing account's: entitlement is keyed on the billing account but
 * membership is keyed on the group, many groups can share one payer, and the
 * two carry separate `name` columns. Naming the payer would tell a
 * sibling-group member that "engagements is enabled on my account", which is
 * true via the shared payer, right next to a refusal about something else.
 */
export interface CreateDenialNames {
  /** `project.group.name`. */
  accountName?: string | null;
  /** `project.name`. */
  projectName?: string | null;
}

export type CreateAffordance =
  /** Render the trigger; the viewer may create. */
  | { kind: 'allowed' }
  /**
   * Render nothing at all. Only for an account without the product: do not
   * advertise something the customer has not bought.
   */
  | { kind: 'hidden' }
  /**
   * Render the trigger, and explain on activation instead of acting. The
   * affordance is legitimately visible to this person in another account, so
   * silence reads as a defect — which is the ENG-5727 report.
   */
  | { kind: 'refused'; message: string };

/** Fallback for a name the query did not select or the api left null. */
const ACCOUNT_FALLBACK = 'this account';
const PROJECT_FALLBACK = 'this project';

function quoted(name: string | null | undefined, fallback: string): string {
  const trimmed = name?.trim();
  return trimmed ? `"${trimmed}"` : fallback;
}

/**
 * The refusal copy, one string per reason.
 *
 * Every reason names what is actually missing AND who can fix it. The second
 * half is not decoration: the refusal is dead-ended without it, and the
 * population this lands on hardest is the project guests — on the project, not
 * in the account — who hit NotGroupMember and cannot act on "you are not a
 * member" alone. A new reason added here without a remedy clause is incomplete.
 *
 * The specificity is why the api returns three membership reasons rather than
 * one.
 *
 * Not spec copy: the prototype Storybook has no story for a creation refusal,
 * so there is nothing to diff this against. Shaped after `ConnectGateBanner`,
 * the one blocking-state precedent in the feature: one instructive sentence,
 * then what to do about it.
 */
export function createDenialMessage(
  reason: EngagementCreateDenialReason,
  names: CreateDenialNames = {}
): string {
  const account = quoted(names.accountName, ACCOUNT_FALLBACK);
  const project = quoted(names.projectName, PROJECT_FALLBACK);

  switch (reason) {
    case EngagementCreateDenialReason.NotGroupOrProjectMember:
      return `You are not a member of the account ${account}, and you are not a member of the project ${project}. Ask an admin of ${account} to add you.`;
    case EngagementCreateDenialReason.NotGroupMember:
      return `You are not a member of the account ${account}. Project membership alone does not allow creating an engagement. Ask an admin of ${account} to add you.`;
    case EngagementCreateDenialReason.NotProjectMember:
      return `You are not a member of the project ${project}. Ask a project owner to add you, or ask an owner of ${account} to create the engagement.`;
    case EngagementCreateDenialReason.AccountNotEntitled:
      // Reachable where a caller states this reason rather than hiding on it —
      // the drawer does, since hiding is meaningless once it is open.
      // Deliberately says nothing about memberships.
      return `${account} does not have engagements enabled.`;
    default:
      // A reason the api added and this table has not learned yet. Refusing
      // without a specific cause still beats letting the click through, and
      // the generic sentence is the one thing every reason has in common.
      return 'You may not create an engagement on this project.';
  }
}

/**
 * The prefix that says a create was attempted and did not happen.
 *
 * Its own sentence rather than a `failed: you are not…` lead-in, so the body
 * above reads correctly with or without it and needs no case surgery.
 *
 * Only `createDenialMessageForCode` adds it, because only that path has an
 * actual failure to report. The two places the copy appears BEFORE any attempt
 * — the consultation header's dialog on click, and the drawer's banner when a
 * project is picked — would be stating something untrue, and "failed" reads as
 * a transient error the customer should retry rather than a refusal they need
 * to act on.
 */
const ATTEMPT_PREFIX = 'Create engagement failed.';

/**
 * What to do about the api's answer.
 *
 * `undefined` means hidden, not allowed. The permissive answer is `null`, an
 * explicit "may create", so a query that forgets to select the field or a
 * project that failed to load must not open the affordance. The api refuses
 * either way; this only decides whether the user meets a button that cannot
 * work.
 */
export function resolveCreateAffordance(
  reason: EngagementCreateDenialReason | null | undefined,
  names: CreateDenialNames = {}
): CreateAffordance {
  if (reason === undefined) return { kind: 'hidden' };
  if (reason === null) return { kind: 'allowed' };
  if (reason === EngagementCreateDenialReason.AccountNotEntitled) return { kind: 'hidden' };
  return { kind: 'refused', message: createDenialMessage(reason, names) };
}

/**
 * The same copy keyed by error code, for a refusal that arrives from the
 * mutation rather than the read field.
 *
 * The backstop, not the primary path: the field answers before the user acts,
 * and this catches the window where a membership changed in between.
 *
 * Keyed off `ERROR_CODES` rather than inline literals so the strings have one
 * home. The code and the enum value are PAIRED, not identical: the code carries
 * the `engagement.` domain prefix and an `actor_` segment that the value drops,
 * because `actor` is server vocabulary and these are read as UI reasons. Adding
 * a reason means adding a code, a value, and a row here.
 */
const CODE_TO_REASON: Record<string, EngagementCreateDenialReason> = {
  [ERROR_CODES.ENGAGEMENT_ACCOUNT_NOT_ENTITLED]: EngagementCreateDenialReason.AccountNotEntitled,
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_GROUP_MEMBER]: EngagementCreateDenialReason.NotGroupMember,
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_PROJECT_MEMBER]: EngagementCreateDenialReason.NotProjectMember,
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_GROUP_OR_PROJECT_MEMBER]:
    EngagementCreateDenialReason.NotGroupOrProjectMember,
};

/** Null when the code is not a create-authority refusal. */
export function createDenialMessageForCode(
  code: string | null | undefined,
  names: CreateDenialNames = {}
): string | null {
  const reason = code ? CODE_TO_REASON[code] : undefined;
  if (!reason) return null;
  // This path IS a failed attempt, by definition: the code came back from the
  // mutation. So it is the one caller that earns the prefix, and callers do not
  // have to reason about which copy they are asking for.
  return `${ATTEMPT_PREFIX} ${createDenialMessage(reason, names)}`;
}
